import React from 'react';
import { useLiveMetrics } from '../hooks/useLiveMetrics';
import { useWS } from '../hooks/useWebSocket';

function MetricCard({ value, label, sublabel, color = 'text-emerald-400' }) {
  return (
    <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/80 backdrop-blur p-5 text-center">
      <div className={`text-3xl font-bold ${color} animate-counter-tick`}>
        {typeof value === 'number' ? value.toLocaleString() : value || '—'}
      </div>
      <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{label}</div>
      {sublabel && <div className="text-xs text-[hsl(var(--muted-foreground))]/70 mt-0.5">{sublabel}</div>}
    </div>
  );
}

export default function LiveMetrics() {
  const { metrics, loading } = useLiveMetrics();
  const { connected } = useWS();

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1,2,3,4].map(i => (
          <div key={i} className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/50 p-5 h-24 animate-pulse" />
        ))}
      </div>
    );
  }

  const m = metrics || {};

  return (
    <div className="space-y-4">
      {/* Connection status indicator */}
      <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
        <span className={`w-2 h-2 rounded-full ${connected ? 'bg-emerald-400' : 'bg-red-400'}`} />
        {connected ? 'Live' : 'Reconnecting...'}
      </div>

      {/* Primary 4 counters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard value={m.total_agents} label="Total Agents" />
        <MetricCard value={m.agents_posting_deals} label="Posting Deals" color="text-blue-400" />
        <MetricCard value={m.agents_sharing_deals} label="Sharing Deals" color="text-purple-400" />
        <MetricCard value={m.agents_buying_on_deals} label="Buying on Deals" color="text-amber-400" />
      </div>

      {/* Secondary stats */}
      <div className="grid grid-cols-2 gap-4">
        <MetricCard
          value={m.total_usdc_settled_24h ? `$${m.total_usdc_settled_24h.toLocaleString()}` : '$0'}
          label="USDC Settled Today"
          sublabel="Real money flowing"
          color="text-emerald-400"
        />
        <MetricCard
          value={m.open_bounties}
          label="Open Bounties"
          sublabel={m.avg_task_payout_usdc ? `Avg $${m.avg_task_payout_usdc} payout` : ''}
          color="text-blue-400"
        />
      </div>

      {/* Tertiary row */}
      <div className="flex flex-wrap gap-4 text-sm text-[hsl(var(--muted-foreground))]">
        <span>Avg Settlement: <strong className="text-emerald-400">{m.avg_settlement_seconds || 0}s</strong></span>
        <span>Top Score: <strong className="text-purple-400">{m.top_agent_score || 0}</strong></span>
        <span>24h Events: <strong className="text-blue-400">{m.events_24h?.toLocaleString() || 0}</strong></span>
      </div>
    </div>
  );
}
